/**
 * アカウントの設定（/account）: 開発者アカウントの閉鎖（2026-09-27 creator/account-close）。
 *
 * お金の画面（pages/money/・Money の持ち物）ではなくアカウントの設定に置く: 閉鎖は「お金の操作」ではなくアカウントの操作で、
 * 返金の依頼はサーバーが閉鎖の中で自動で出す（本人が選ぶものは無い）。
 *
 * 流れ（API の正は apps/server/src/modules/platform/accountClose.ts・判断は ../accountClose.ts）:
 *   1. 必ず全部見せる 3 つ（再開できない / 前払いの残高の扱い / 出金に期限は無い・未請求財産）と、閉鎖後にできること・できないこと
 *   2. 動いているゲームの一覧と削除（サーバーは 409 projects_remain で止める = 黙って消さない。以前はダッシュボードに
 *      ゲームの削除が無く、409 の「先に消して」の行き先が無かったので、ここに slug を打つ確認つきで置く）
 *   3. 打ち込みの確認（メールアドレスか CLOSE）+ パスワード → DELETE /v1/developers/me
 *   4. 成功 = 全セッション失効。「出金のためにログインできる」を見せてからログアウト
 *
 * 閉鎖済み（developer.closedAt）でログインした人には、閉鎖の画面の代わりに「閉鎖済み・出金はできる」を出す。
 *
 * 2026-09-27 creator/dev-oauth-ui: 「ログイン方法」の欄（pages/AccountLoginMethods.tsx）を閉鎖の上に置く（通知メール
 * 「ログイン方法が追加 / 削除されました」のボタンがこの画面に向く）。閉鎖の再認証は、パスワードを持たない人（Google 等だけ）は
 * 共通の再認証のダイアログ（components/Reauth.tsx）で「Google で確認」→ 戻ったら最後の 1 回を押してもらう。
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, setToken, type ApiError, type ProjectSummary } from '../api';
import { canSubmitClose, CLOSE_WORD, closeFailureKind, deleteGameConfirmOk } from '../accountClose';
import { authFormProps, hiddenUsernameProps, passwordFieldProps } from '../authForms';
import { CLI } from '../cliCommand';
import { ConfirmDialog, ErrorState, PageHeader, Spinner } from '../components/ui';
import { useResource } from '../hooks';
import { errorText, formatDate, useT } from '../i18n';
import { useSession } from '../session';
import { TopBar } from './ProjectsPage';
import { LoginMethodsCard } from './AccountLoginMethods';
import { useReauth, useReauthFailureText } from '../components/Reauth';
import { SECRET_KEYS_PATH } from '../secretKeys';
import { isLaunchHidden, isMoneyNavHidden } from '../launchHidden';

/**
 * 売上と出金（payouts）を閉じているか（2026-09-30 console/words-pass・launchHidden.ts の 1 つの切り替えに従う）。
 * 閉じている間は、閉鎖の説明から「売上から入れた分」「閉鎖後も出金できる」を外す（出金の画面へ行けない・売上が無いのに約束しない）。
 * 一覧から payouts を外せば、元の 3 つの説明と「出金へ」がそのまま戻る
 */
const PAYOUTS_HIDDEN = isLaunchHidden('payouts');

export function AccountPage() {
  const { developer } = useSession();
  const { t } = useT();
  // 閉鎖に成功した後の画面（セッションはもう無いので、この画面から API を呼ばない）
  const [done, setDone] = useState(false);
  if (!developer) return null;

  if (done) return <ClosedDone />;

  return (
    <div className="page-wrap">
      <TopBar />
      <main className="container narrow" id="main">
        <PageHeader eyebrow={t('account.eyebrow')} title={t('account.title')} />
        <p className="muted">{t('account.signedInAs', { email: developer.email })}</p>
        {!developer.closedAt && <LoginMethodsCard />}
        {/* サーバーの鍵の一覧・まとめて作り直す（pages/AccountSecretKeys.tsx）への入口。閉鎖済みは鍵を作れない（プロジェクトも無い）ので出さない */}
        {!developer.closedAt && (
          <section className="card">
            <h2 className="card-title">{t('skList.cardTitle')}</h2>
            <p className="muted">{t('skList.cardBody')}</p>
            <Link to={SECRET_KEYS_PATH} className="btn">
              {t('skList.cardLink')}
            </Link>
          </section>
        )}
        {developer.closedAt ? (
          <AlreadyClosed closedAt={developer.closedAt} />
        ) : (
          // hasPassword が undefined（古いサーバー）= パスワードを持っているとみなす（今までどおりの欄）
          <CloseAccount email={developer.email} passwordless={developer.hasPassword === false} onClosed={() => setDone(true)} />
        )}
      </main>
    </div>
  );
}

/** 閉鎖済みの人（出金のためにログインしてきた）に出す説明 */
function AlreadyClosed({ closedAt }: { closedAt: string }) {
  const { t } = useT();
  return (
    <section className="card">
      <h2 className="card-title">{t('account.closed.title')}</h2>
      <p>{t(PAYOUTS_HIDDEN ? 'account.closed.bodyLaunch' : 'account.closed.body', { date: formatDate(closedAt) })}</p>
      {/* 出金（payouts）を見せない間はボタンを出さない（2026-09-29 creator/launch-money・押しても前払い残高へ戻されるだけ） */}
      {!isMoneyNavHidden('withdraw') && (
        <Link to="/money/live/withdraw" className="btn btn-primary">
          {t('account.closed.withdraw')}
        </Link>
      )}
    </section>
  );
}

function CloseAccount({ email, passwordless, onClosed }: { email: string; passwordless: boolean; onClosed: () => void }) {
  const { t, tr } = useT();
  const reauth = useReauth();
  const failureText = useReauthFailureText();
  // 「Google で確認」から戻ってきた（閉鎖の送信の途中だった）。打ち込みの確認は出る前に済んでいる = 最後の 1 回だけ押してもらう
  // （取り消せない操作なので、戻った瞬間に自動では送らない）
  const resumed = reauth.pending?.action === CLOSE_ACTION ? reauth.pending : null;
  // 戻りで失敗した（別のアカウントを選んだ・キャンセル）。画面に写してからメモリの方は捨てる（別の画面へ行って戻ったときに二度出さない）
  const [resumeFailure] = useState(() => (reauth.failure?.action === CLOSE_ACTION ? reauth.failure : null));
  const { consume } = reauth;
  useEffect(() => {
    if (resumeFailure) consume();
  }, [resumeFailure, consume]);
  const projects = useResource<{ projects: ProjectSummary[] }>('/v1/projects');
  const [typed, setTyped] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const list = projects.data?.projects ?? [];
  // 一覧が読めるまでは押させない（残っているか分からない = 409 になりうる要求を送らない）
  const projectsLeft = projects.data ? list.length : 1;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!resumed && !canSubmitClose({ typed, email, password, projectsLeft, busy, passwordless })) return;
    if (resumed && (busy || projectsLeft > 0)) return;
    setBusy(true);
    setError(null);
    try {
      if (passwordless || resumed) {
        // パスワードの無い人: まず証明なしで送り、400 reauthentication_required の details（使える方法）で確認のダイアログを出して送り直す。
        // 戻ってきた証明（resumed）があれば run がそれを使う。取り消したら null = 何もしない
        const r = await reauth.run((proof) => api('/v1/developers/me', { method: 'DELETE', body: proof }).then(() => true), { action: CLOSE_ACTION });
        if (!r) return;
      } else {
        await api('/v1/developers/me', { method: 'DELETE', body: { password } });
      }
      // サーバーが全セッションを失効させた。死んだトークンを端末に残さない（次の要求で 401 → ログイン画面へ飛ばされ、
      // 完了の説明を読む前に消えるのも防ぐ = 完了の画面は API を呼ばない）
      setToken(null);
      onClosed();
    } catch (err) {
      const e2 = err as ApiError;
      const kind = closeFailureKind(e2);
      if (kind === 'projects_remain') {
        // 別の端末・CLI でゲームが作られていた等。一覧を読み直して削除の行を出す
        projects.reload();
        setError(t('account.close.gamesRemainAlert'));
      } else if (kind === 'wrong_password') {
        setError(t('account.close.wrongPassword'));
        setPassword('');
      } else {
        setError(errorText(e2));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <section className="card">
        <h2 className="card-title">{t('account.close.title')}</h2>
        <p>{t(PAYOUTS_HIDDEN ? 'account.close.leadLaunch' : 'account.close.lead')}</p>
        {/* 管理判断: この 3 つは必ず全部見せる（折りたたまない）。出金を閉じている間は 3 つめ（出金の期限）が無い = 2 つ（下の PAYOUTS_HIDDEN） */}
        <div className="stack">
          <div className="alert alert-error">
            <strong>{t('account.close.p1Title')}</strong>
            <span>{t('account.close.p1Body')}</span>
          </div>
          <div className="alert alert-warn">
            <strong>{t(PAYOUTS_HIDDEN ? 'account.close.p2TitleLaunch' : 'account.close.p2Title')}</strong>
            <span>{t(PAYOUTS_HIDDEN ? 'account.close.p2BodyLaunch' : 'account.close.p2Body')}</span>
          </div>
          {!PAYOUTS_HIDDEN && (
            <div className="alert alert-info">
              <strong>{t('account.close.p3Title')}</strong>
              <span>{t('account.close.p3Body')}</span>
            </div>
          )}
        </div>
        <h3 className="section-title">{t('account.close.afterTitle')}</h3>
        <ul className="small">
          <li>{t(PAYOUTS_HIDDEN ? 'account.close.afterCanLaunch' : 'account.close.afterCan')}</li>
          <li>{t('account.close.afterCannot')}</li>
          <li>{t('account.close.afterErase')}</li>
        </ul>
        <p className="small muted">{tr('account.close.aiNote', { cmd: <code>{CLI} account close</code> })}</p>
      </section>

      <section className="card">
        <h2 className="card-title">{t('account.close.gamesTitle')}</h2>
        {projects.loading && <Spinner />}
        {projects.error && <ErrorState error={projects.error} onRetry={projects.reload} />}
        {projects.data && list.length === 0 && <p className="muted">✓ {t('account.close.gamesNone')}</p>}
        {list.length > 0 && (
          <>
            <p>{t('account.close.gamesBody')}</p>
            <ul className="stack">
              {list.map((p) => (
                <GameRow key={p.id} project={p} onDeleted={projects.reload} />
              ))}
            </ul>
          </>
        )}
      </section>

      {reauth.dialog}
      <section className="card">
        <h2 className="card-title">{t('account.close.formTitle')}</h2>
        {resumeFailure && (
          <div className="alert alert-error" role="alert">
            {failureText(resumeFailure)}
          </div>
        )}
        {resumed && (
          <div className="alert alert-warn stack" role="status">
            <strong>{t('account.close.resumedTitle')}</strong>
            <span>{t('account.close.resumedBody')}</span>
            <div>
              <button type="button" className="btn btn-danger" disabled={busy || projectsLeft > 0} onClick={() => void submit()}>
                {busy ? t('common.working') : t('account.close.submit')}
              </button>{' '}
              <button type="button" className="btn btn-ghost" onClick={reauth.consume} disabled={busy}>
                {t('common.cancel')}
              </button>
            </div>
          </div>
        )}
        {/* 2026-09-27 creator/auth-forms: パスワード管理が「このアカウントの今のパスワード」を入れられる形に
            （post のフォーム・見えないアカウント名の欄・current-password。属性は authForms.ts） */}
        <form {...authFormProps('closeAccount', '/account')} onSubmit={(e) => void submit(e)} className="stack">
          {/* どのアカウントのパスワードかを管理に教える欄（見えない）。打ち込みの確認の欄にメールを入れさせないためにも、
              username はこちらだけに付ける（確認の欄は autocomplete=off のまま＝本人が打つ） */}
          <input {...hiddenUsernameProps(email)} />
          <label className="field" htmlFor="close-confirm">
            <span>{tr('account.close.typeConfirm', { email: <code>{email}</code>, word: <code>{CLOSE_WORD}</code> })}</span>
            <input id="close-confirm" name="close-confirm" value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoCapitalize="off" spellCheck={false} disabled={projectsLeft > 0} />
          </label>
          {passwordless ? (
            // パスワードを持たない人（Google 等だけ）: 押した後に確認のダイアログ（components/Reauth.tsx）で「Google で確認」
            <p className="small muted">{t('account.close.oauthConfirmNote')}</p>
          ) : (
            <label className="field" htmlFor="password">
              <span>{t('account.close.password')}</span>
              <input {...passwordFieldProps('current')} value={password} onChange={(e) => setPassword(e.target.value)} disabled={projectsLeft > 0} />
            </label>
          )}
          {projects.data && list.length > 0 && <p className="small muted">{t('account.close.gamesRemainAlert')}</p>}
          {error && (
            <div className="alert alert-error" role="alert">
              {error}
            </div>
          )}
          <div>
            <button type="submit" className="btn btn-danger" disabled={!canSubmitClose({ typed, email, password, projectsLeft, busy, passwordless })}>
              {busy ? t('common.working') : t('account.close.submit')}
            </button>
          </div>
        </form>
      </section>
    </>
  );
}

/** 再認証の「戻った後に続ける操作」の名前（components/Reauth.tsx の action） */
const CLOSE_ACTION = 'close';

/** 残っているゲーム 1 行と削除（slug を打つ確認。DELETE /v1/projects/:id = 鍵はすぐ失効） */
function GameRow({ project, onDeleted }: { project: ProjectSummary; onDeleted: () => void }) {
  const { t, tr } = useT();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = () => {
    setOpen(false);
    setTyped('');
    setError(null);
  };
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api(`/v1/projects/${project.id}`, { method: 'DELETE' });
      close();
      onDeleted();
    } catch (err) {
      setError(errorText(err as ApiError));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="card-head">
      <span>
        <Link to={`/p/${project.id}/development/overview`}>{project.name}</Link> <code className="small">{project.slug}</code>
      </span>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => setOpen(true)}>
        {t('account.close.deleteGame')}
      </button>
      <ConfirmDialog
        open={open}
        title={t('account.close.deleteGameTitle', { name: project.name })}
        confirmLabel={t('account.close.deleteGameConfirm')}
        tone="danger"
        busy={busy}
        confirmDisabled={!deleteGameConfirmOk(typed, project.slug)}
        onClose={close}
        onConfirm={() => void remove()}
      >
        <p>{t('account.close.deleteGameBody')}</p>
        {/* 2026-10-06 founder の決定: 削除から 30 日で slug が空く（サーバーの lib/projectSlugs.ts の SLUG_RELEASE_DAYS）。貼ったリンクが他人のページに飛ぶことを消す前に具体的に伝える */}
        <p>{tr('account.close.deleteGameSlug', { slug: <code>{project.slug}</code> })}</p>
        <label className="field">
          <span>{tr('account.close.typeSlug', { slug: <code>{project.slug}</code> })}</span>
          <input value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" autoCapitalize="off" spellCheck={false} />
        </label>
        {error && (
          <div className="alert alert-error" role="alert">
            {error}
          </div>
        )}
      </ConfirmDialog>
    </li>
  );
}

/** 閉鎖の直後。セッションはもう無いので TopBar（API を呼ぶ帯がある）は出さない。OK でログイン画面へ */
function ClosedDone() {
  const { t } = useT();
  const { signOut } = useSession();
  return (
    <div className="page-wrap">
      <main className="container narrow" id="main">
        <section className="card">
          <h1 className="card-title">{t('account.done.title')}</h1>
          <p>{t('account.done.body')}</p>
          {!PAYOUTS_HIDDEN && <div className="alert alert-info">{t('account.done.withdraw')}</div>}
          {/* signOut: サーバーのログアウトは失効済みで失敗するが、signOut はそれを無視して端末の状態を消す */}
          <button type="button" className="btn btn-primary" onClick={() => void signOut()}>
            {t('account.done.ok')}
          </button>
        </section>
      </main>
    </div>
  );
}
